/**
 * Tennis Sync — client-side orchestrator.
 *
 * POSTs to /api/tennis-sync (the Vercel serverless function), and on a 200
 * response triggers `snapshotLeaderboardAfterSync` to refresh the league
 * leaderboard. At this milestone, the snapshot only captures CURRENT squad
 * totals (no per-player recompute yet) — the recompute step is wired in
 * by a later milestone, into this same helper.
 *
 * Dependency injection: all external boundaries (fetch, ID token retrieval,
 * leaderboard snapshot) are injectable via the `deps` argument so the
 * orchestrator is fully unit-testable without firebase or live HTTP.
 */

export type TriggerTennisSyncParams = {
  tournamentKey: number;
  dateStart: string;
  dateStop: string;
};

export type TriggerTennisSyncResult = {
  status: number;
  ok: boolean;
  body: unknown;
};

export type TriggerTennisSyncDeps = {
  fetch?: typeof fetch;
  getIdToken?: () => Promise<string>;
  snapshotLeaderboardAfterSync?: (leagueId: string) => Promise<void>;
};

/**
 * Refreshes the league leaderboard after a successful API sync.
 *
 * Today this just creates a snapshot of current squad totals. When the
 * per-player point recompute lands (next milestone), it will be added
 * here so the call site (triggerTennisSync) stays the same.
 */
export async function snapshotLeaderboardAfterSync(leagueId: string): Promise<void> {
  // Lazy import to avoid pulling Firebase into modules that don't need it
  // (and to keep the unit test for triggerTennisSync free of Firebase init).
  const { leaderboardSnapshotService } = await import('./firestore');
  await leaderboardSnapshotService.create(leagueId);
}

async function defaultGetIdToken(): Promise<string> {
  const { getAuth } = await import('firebase/auth');
  const user = getAuth().currentUser;
  if (!user) {
    throw new Error('Not authenticated — no current Firebase user');
  }
  return user.getIdToken();
}

/**
 * Trigger a tennis API sync for a league + tournament + date range.
 *
 * Behavior:
 *  - POST /api/tennis-sync with `Authorization: Bearer <idToken>` and the
 *    JSON body `{leagueId, tournamentKey, dateStart, dateStop}`.
 *  - `console.log('Tennis API call status:', status)` for every response
 *    (so the admin sees 200 in DevTools console — explicit user requirement).
 *  - On 200 OK only: invoke `snapshotLeaderboardAfterSync(leagueId)`.
 *  - On any non-2xx response or a fetch error: do NOT touch the leaderboard.
 *
 * Returns `{status, ok, body}` so the caller can show a success/error UI.
 */
export async function triggerTennisSync(
  leagueId: string,
  params: TriggerTennisSyncParams,
  deps: TriggerTennisSyncDeps = {}
): Promise<TriggerTennisSyncResult> {
  const _fetch = deps.fetch || fetch;
  const _getIdToken = deps.getIdToken || defaultGetIdToken;
  const _snapshot = deps.snapshotLeaderboardAfterSync || snapshotLeaderboardAfterSync;

  const token = await _getIdToken();

  const res = await _fetch('/api/tennis-sync', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      leagueId,
      tournamentKey: params.tournamentKey,
      dateStart: params.dateStart,
      dateStop: params.dateStop,
    }),
  });

  // eslint-disable-next-line no-console
  console.log('Tennis API call status:', res.status);

  const body = await res.json();

  if (res.ok) {
    await _snapshot(leagueId);
  }

  return {
    status: res.status,
    ok: res.ok,
    body,
  };
}
